import * as React from "react";
import { observer } from "mobx-react-lite";
import { mk8sDryRun } from "./../../../../../mobxStores/dryRun/mk8s";
import { Mk8sDraftMobx } from "./../../../../../mst/stores/mk8s.draft";
import { homeLink, request } from "./../../../../../services/cpln";
import { DryRunAlert } from "./../../../dryRunAlert";
import { MoreHorizontal, PlusCircle } from "react-feather";
import { NGKindSelect } from "./../../../../../newcomponents/select/ngkindselect";
import { useNGFormContext } from "../../../../../reactContexts/ngFormContext";
import { NGFormElement } from "../../../../../newcomponents/ngformelement/ngformelement";
import { NGProviderBrowser } from "../../../ngProviderBrowser";
import { NGFormLabel } from "../../../../../newcomponents/text/formLabel";
import { NGProviderCreateOpaqueSecret } from "../../../ngProviderCreateOpaqueSecret";
import NGAlert from "../../../../../newcomponents/alert";
import { NGInputListMst } from "../../../../../newcomponents/inputList/inputListMst";
import { NGInput } from "../../../../../newcomponents/input/input";
import { NGButton } from "../../../../../newcomponents/button/Button";
import { getDryRunErrorText } from "../../../getDryRunErrorText";

interface Props {
  mk8sDraft: Mk8sDraftMobx;
}
const Mk8sCreateProviderLinodeRaw: React.FC<Props> = ({ mk8sDraft }) => {
  const l = mk8sDraft.provider_linode!;
  const formData = useNGFormContext();

  const [browserKey, setBrowserKey] = React.useState("");

  const authorizedUsersDryRun = getDryRunErrorText({
    dryRunResponse: mk8sDryRun.response,
    path: "spec.provider.linode.authorizedUsers",
    paths: l.authorizedUsers.items.map((_, idx) => `spec.provider.linode.authorizedUsers[${idx}]`),
  });

  return (
    <>
      <div>
        <div className="flex gap-2 items-start">
          <NGFormElement
            name={"linode.region"}
            label={l.region.label}
            value={l.region.value}
            onChange={l.region.setValue}
            required={l.region.isRequired}
            error={l.region.error}
            innerButtons={[
              {
                title: "Browse",
                onClick: () => {
                  setBrowserKey("regions");
                  formData.set("linode.region", { touched: true });
                },
                render: () => <MoreHorizontal />,
              },
            ]}
          />
          <div className="mt-6">
            <DryRunAlert
              key={mk8sDryRun.key}
              canShow={formData.get("linode.region").touched}
              dryRunResponse={mk8sDryRun.response}
              onFix={l.region.setValue}
              path={"spec.provider.linode.region"}
            />
          </div>
        </div>

        <div className="mb-4">
          <NGFormLabel required invalid={!l.tokenSecretName}>
            Linode Token Secret
          </NGFormLabel>
          <div className="mb-2 flex items-start gap-2">
            <NGKindSelect
              name={"linode.tokenSecretName"}
              style={{ width: 450 }}
              value={l.tokenSecretName}
              invalid={!l.tokenSecretName}
              onChange={(value) => l.setTokenSecretName(value)}
              kind={"secret"}
              queries={[{ property: "type", value: "opaque" }]}
              buttons={[
                {
                  title: "Create",
                  render: () => <PlusCircle className="h-4" />,
                  onClick: () => setBrowserKey("createSecret"),
                },
              ]}
            />
            <DryRunAlert
              key={mk8sDryRun.key}
              canShow={formData.get("linode.tokenSecretName").touched}
              dryRunResponse={mk8sDryRun.response}
              path={"spec.provider.linode.tokenSecretLink"}
            />
          </div>
          <NGAlert
            style={{ width: 450 }}
            render={() => (
              <div className="flex flex-col text-sm">
                <span>An opaque secret that contains your Linode token.</span>
                <span>It will be used to manage servers in your Linode account.</span>
              </div>
            )}
          />
        </div>

        <div className="flex gap-2 items-start">
          <NGFormElement
            name={"linode.image"}
            label={l.image.label}
            value={l.image.value}
            onChange={l.image.setValue}
            required={l.image.isRequired}
            error={l.image.error}
            innerButtons={[
              {
                title: "Browse",
                onClick: () => {
                  setBrowserKey("images");
                  formData.set("linode.image", { touched: true });
                },
                render: () => <MoreHorizontal />,
              },
            ]}
          />
          <div className="mt-6">
            <DryRunAlert
              key={mk8sDryRun.key}
              canShow={formData.get("linode.image").touched}
              dryRunResponse={mk8sDryRun.response}
              onFix={l.image.setValue}
              path={"spec.provider.linode.image"}
            />
          </div>
        </div>

        <div className="flex gap-2 items-start">
          <NGFormElement
            name={"linode.vpcId"}
            label={l.vpcId.label}
            value={l.vpcId.value}
            onChange={l.vpcId.setValue}
            required={l.vpcId.isRequired}
            error={l.vpcId.error}
            innerButtons={[
              {
                title: "Browse",
                onClick: () => {
                  setBrowserKey("vpcs");
                  formData.set("linode.vpcId", { touched: true });
                },
                render: () => <MoreHorizontal />,
              },
            ]}
          />
          <div className="mt-6">
            <DryRunAlert
              key={mk8sDryRun.key}
              canShow={formData.get("linode.vpcId").touched}
              dryRunResponse={mk8sDryRun.response}
              onFix={l.vpcId.setValue}
              path={"spec.provider.linode.vpcId"}
            />
          </div>
        </div>

        <div className="flex gap-2 items-start">
          <NGFormElement
            name={"linode.firewallId"}
            label={l.firewallId.label}
            value={l.firewallId.value}
            onChange={l.firewallId.setValue}
            required={l.firewallId.isRequired}
            error={l.firewallId.error}
            innerButtons={[
              {
                title: "Browse",
                onClick: () => {
                  setBrowserKey("firewalls");
                  formData.set("linode.firewallId", { touched: true });
                },
                render: () => <MoreHorizontal />,
              },
            ]}
          />
          <div className="mt-6">
            <DryRunAlert
              key={mk8sDryRun.key}
              canShow={formData.get("linode.firewallId").touched}
              dryRunResponse={mk8sDryRun.response}
              onFix={l.firewallId.setValue}
              path={"spec.provider.linode.firewallId"}
            />
          </div>
        </div>

        <NGInputListMst
          data={l.authorizedUsers}
          className="mb-8"
          styles={{ container: { width: 450, minWidth: 450 } }}
          label="Authorized Users"
          labelRender={() => {
            return (
              <>
                <NGButton className="ml-1" size={"small"} variant={"secondary"} onClick={() => setBrowserKey("users")}>
                  Browse
                </NGButton>
              </>
            );
          }}
          firstInput={(i, index) => {
            let invalid = false;
            if (authorizedUsersDryRun.message.split("[")[1]?.split("]")[0] === String(index)) {
              invalid = true;
            }

            return <NGInput invalid={invalid} value={i.firstValue} onChange={(e) => i.setFirstValue(e.target.value)} />;
          }}
          invalid={authorizedUsersDryRun.severity === "error"}
          error={authorizedUsersDryRun.severity === "error" ? authorizedUsersDryRun.message : ""}
          warning={authorizedUsersDryRun.severity === "error" ? "" : authorizedUsersDryRun.message}
        />
      </div>

      {browserKey === "createSecret" ? (
        <NGProviderCreateOpaqueSecret
          onOk={(secretName: string) => l.setTokenSecretName(secretName)}
          onClose={() => setBrowserKey("")}
          title={"Create Linode Token Secret"}
          dataLabel={"Linode Token"}
        />
      ) : null}
      {browserKey === "regions" ? (
        <NGProviderBrowser
          title="Browse Linode Regions"
          initialValue={l.region.value}
          fetchData={async () => {
            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "regions",
                cluster: mk8sDraft.asObject,
              },
            });
            return data.data;
          }}
          onOk={(value) => l.region.setValue(value as string)}
          onClose={() => setBrowserKey("")}
          labels={["Name", "Description"]}
          getData={(item: any) => [item.id, item.id, item.label]}
        />
      ) : null}
      {browserKey === "firewalls" ? (
        <NGProviderBrowser
          title="Browse Linode Firewalls"
          initialValue={l.firewallId.value}
          fetchData={async () => {
            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "firewalls",
                cluster: mk8sDraft.asObject,
              },
            });
            return data.data;
          }}
          onOk={(value) => l.firewallId.setValue(String(value as string))}
          onClose={() => setBrowserKey("")}
          labels={["Name", "Description"]}
          getData={(item: any) => [item.id, item.id, item.label]}
        />
      ) : null}
      {browserKey === "images" ? (
        <NGProviderBrowser
          title="Browse Linode Images"
          initialValue={l.image.value}
          fetchData={async () => {
            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "images",
                cluster: mk8sDraft.asObject,
              },
            });
            return data.data;
          }}
          onOk={(value) => l.image.setValue(value as string)}
          onClose={() => setBrowserKey("")}
          labels={["Name", "Description"]}
          getData={(item: any) => [item.id, item.id, item.label]}
        />
      ) : null}
      {browserKey === "vpcs" ? (
        <NGProviderBrowser
          title="Browse Linode VPCs"
          initialValue={l.vpcId.value}
          fetchData={async () => {
            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "vpcs",
                cluster: mk8sDraft.asObject,
              },
            });
            return data.data;
          }}
          onOk={(value) => l.vpcId.setValue(String(value as string))}
          onClose={() => setBrowserKey("")}
          labels={["Name", "Description"]}
          getData={(item: any) => [item.id, item.id, item.label]}
        />
      ) : null}
      {browserKey === "users" ? (
        <NGProviderBrowser
          title="Browse Linode Users"
          initialValue={l.authorizedUsers.items.map((i) => i.firstValue)}
          fetchData={async () => {
            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "users",
                cluster: mk8sDraft.asObject,
              },
            });
            return data.data;
          }}
          multipleChoice
          onOk={(_value) => {
            const values: string[] = _value as any;
            for (const value of values) {
              if (l.authorizedUsers.items.some((i) => i.firstValue === value)) {
                continue;
              }
              l.authorizedUsers.add();
              const i = l.authorizedUsers.items[l.authorizedUsers.items.length - 1];
              i.setFirstValue(value);
            }
          }}
          onClose={() => setBrowserKey("")}
          labels={["Username", "Email"]}
          getData={(item: any) => [item.username, item.username, item.email]}
        />
      ) : null}
    </>
  );
};

export const Mk8sCreateProviderLinode = observer(Mk8sCreateProviderLinodeRaw);
